<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <button class="btn btn-icon btn-back" @click="$router.push('/client/members')">
          <i class="ri-arrow-left-line"></i>
        </button>
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="page-container">
      <div class="tab-bar">
        <button
          class="tab-item"
          :class="{ active: activeTab === 'import' }"
          @click="switchTab('import')"
        >
          <i class="ri-upload-2-line"></i>
          匯入紀錄
        </button>
        <button
          class="tab-item"
          :class="{ active: activeTab === 'export' }"
          @click="switchTab('export')"
        >
          <i class="ri-download-2-line"></i>
          匯出紀錄
        </button>
      </div>

      <!-- ===================== 匯出紀錄 Tab ===================== -->
      <template v-if="activeTab === 'export'">
        <div v-if="exportLoading && exportJobs.length === 0" class="loading-state">
          <i class="ri-loader-4-line spin"></i>
          載入中...
        </div>

        <div v-else-if="exportError" class="empty-state">
          <i class="ri-error-warning-line"></i>
          <p>{{ exportError }}</p>
          <button class="btn btn-secondary" @click="fetchExportJobs">重新載入</button>
        </div>

        <div v-else-if="exportJobs.length === 0" class="empty-state">
          <i class="ri-file-download-line"></i>
          <p>尚無匯出紀錄</p>
        </div>

        <div v-else>
          <div class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>匯出編號</th>
                  <th>狀態</th>
                  <th>篩選條件</th>
                  <th>筆數</th>
                  <th>建立時間</th>
                  <th>完成時間</th>
                  <th>檔案效期</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="job in exportJobs" :key="job.jobNumber">
                  <td class="job-number">{{ job.jobNumber }}</td>
                  <td>
                    <span :class="getExportStatusClass(job.status)">
                      {{ getExportStatusText(job.status) }}
                    </span>
                  </td>
                  <td class="filters-cell" :title="getFilterSummary(job.filters)">
                    {{ getFilterSummary(job.filters) }}
                  </td>
                  <td>{{ job.totalRows != null ? job.totalRows.toLocaleString('zh-TW') : '-' }}</td>
                  <td>{{ formatDate(job.createdAt) }}</td>
                  <td>{{ formatDate(job.completedAt) }}</td>
                  <td>
                    <span v-if="!job.fileExpiresAt">-</span>
                    <span v-else-if="isExportFileExpired(job)" class="expired-text">已過期</span>
                    <span v-else>{{ formatDate(job.fileExpiresAt) }}</span>
                  </td>
                  <td>
                    <div class="action-group">
                      <button
                        v-if="job.status === 'completed' && !isExportFileExpired(job)"
                        class="btn btn-sm btn-outline-secondary"
                        :disabled="downloadingExport === job.jobNumber"
                        @click="downloadExport(job)"
                      >
                        <i class="ri-download-2-line"></i>
                        {{ downloadingExport === job.jobNumber ? '下載中...' : '下載 CSV' }}
                      </button>
                      <span v-else-if="job.status === 'failed'" class="error-summary-text" :title="job.errorSummary">
                        {{ job.errorSummary || '-' }}
                      </span>
                      <span v-else>-</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 匯出分頁 -->
          <ListPagination
            v-if="exportTotalPages > 1"
            :page="exportCurrentPage"
            :total-pages="exportTotalPages"
            :total="exportTotal"
            @change="goToExportPage"
          />
        </div>
      </template>

      <!-- ===================== 匯入紀錄 Tab ===================== -->
      <template v-else>
      <div v-if="loading && jobs.length === 0" class="loading-state">
        <i class="ri-loader-4-line spin"></i>
        載入中...
      </div>

      <div v-else-if="error" class="empty-state">
        <i class="ri-error-warning-line"></i>
        <p>{{ error }}</p>
        <button class="btn btn-secondary" @click="fetchJobs">重新載入</button>
      </div>

      <div v-else-if="jobs.length === 0" class="empty-state">
        <i class="ri-file-upload-line"></i>
        <p>尚無匯入紀錄</p>
      </div>

      <div v-else>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>批次號</th>
                <th>檔案名稱</th>
                <th>狀態</th>
                <th>總筆數</th>
                <th>新增</th>
                <th>更新</th>
                <th>略過</th>
                <th>失敗</th>
                <th>重複處理</th>
                <th>匯入時間</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="job in jobs" :key="job.jobNumber">
                <td class="job-number">{{ job.jobNumber }}</td>
                <td>{{ job.fileName || '-' }}</td>
                <td>
                  <span :class="getStatusClass(job.status)">
                    {{ getStatusText(job.status) }}
                  </span>
                </td>
                <td>{{ job.totalRows }}</td>
                <td class="count-cell success">{{ job.createdCount }}</td>
                <td class="count-cell info">{{ job.updatedCount }}</td>
                <td class="count-cell muted">{{ job.skippedCount }}</td>
                <td class="count-cell danger">{{ job.failedCount }}</td>
                <td>{{ getDuplicateStrategyText(job.duplicateStrategy) }}</td>
                <td>{{ formatDate(job.createdAt) }}</td>
                <td>
                  <div class="action-group">
                    <button
                      v-if="job.failedCount > 0"
                      class="btn btn-sm btn-outline-secondary"
                      @click="showErrors(job)"
                    >
                      <i class="ri-error-warning-line"></i>
                      錯誤明細
                    </button>
                    <button
                      v-if="!job.revokeStatus && job.createdCount > 0"
                      class="btn btn-sm btn-outline-danger"
                      :disabled="revoking === job.jobNumber"
                      @click="startRevoke(job)"
                    >
                      <i class="ri-delete-back-2-line"></i>
                      撤銷匯入
                    </button>
                    <span v-if="job.revokeStatus" class="badge badge-secondary revoke-badge">
                      <i class="ri-check-line"></i>
                      已撤銷
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分頁 -->
        <ListPagination
          v-if="totalPages > 1"
          :page="currentPage"
          :total-pages="totalPages"
          :total="total"
          @change="goToPage"
        />
      </div>
      </template>
    </div>

    <!-- 撤銷確認 Modal -->
    <div v-if="revokeModal.show" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3>撤銷匯入確認</h3>
        </div>
        <div class="modal-body">
          <!-- Step 1: 預覽載入中 -->
          <div v-if="revokeModal.step === 'loading'" class="loading-state">
            <i class="ri-loader-4-line spin"></i>
            分析影響中...
          </div>

          <!-- Step 1: 預覽結果 -->
          <div v-else-if="revokeModal.step === 'preview'">
            <p class="revoke-desc">
              批次號 <strong>{{ revokeModal.jobNumber }}</strong> 共匯入
              <strong>{{ revokeModal.preview.totalImported }}</strong> 筆會員，撤銷後：
            </p>
            <div class="revoke-stats">
              <div class="revoke-stat danger">
                <span class="stat-num">{{ revokeModal.preview.toDelete }}</span>
                <span class="stat-label">將被刪除<small>（無活動紀錄）</small></span>
              </div>
              <div class="revoke-stat warning">
                <span class="stat-num">{{ revokeModal.preview.toDeactivate }}</span>
                <span class="stat-label">改為停用<small>（有點數/訂單/優惠券）</small></span>
              </div>
              <div class="revoke-stat muted">
                <span class="stat-num">{{ revokeModal.preview.toSkip }}</span>
                <span class="stat-label">略過<small>（已合併會員，無法撤銷）</small></span>
              </div>
            </div>
            <div v-if="revokeModal.preview.skippedMembers?.length > 0" class="skipped-list">
              <p class="skipped-title">以下已合併會員將略過：</p>
              <div v-for="m in revokeModal.preview.skippedMembers" :key="m.memberId" class="skipped-item">
                {{ m.name }}（{{ m.phone }}）— {{ m.reason }}
              </div>
            </div>
            <p class="revoke-warning">
              <i class="ri-alert-line"></i>
              此操作無法還原。確定要執行撤銷嗎？
            </p>
          </div>

          <!-- Step 2: 執行中 -->
          <div v-else-if="revokeModal.step === 'executing'" class="loading-state">
            <i class="ri-loader-4-line spin"></i>
            撤銷執行中，請稍候...
          </div>
        </div>
        <div v-if="revokeModal.step !== 'loading' && revokeModal.step !== 'executing'" class="modal-footer">
          <button class="btn btn-secondary" @click="cancelRevoke">取消</button>
          <button
            class="btn btn-danger"
            :disabled="revokeModal.step !== 'preview'"
            @click="confirmRevoke"
          >
            確認撤銷
          </button>
        </div>
      </div>
    </div>

    <!-- 錯誤明細 Modal -->
    <div v-if="errorModalJob" class="modal-overlay" @click.self="errorModalJob = null">
      <div class="modal-container">
        <div class="modal-header">
          <h3>錯誤明細 — {{ errorModalJob.jobNumber }}</h3>
          <button type="button" class="modal-close" @click="errorModalJob = null">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="loadingErrors" class="loading-state">
            <i class="ri-loader-4-line spin"></i>
            載入中...
          </div>
          <div v-else-if="jobErrors.length === 0" class="empty-state-small">
            無錯誤資料
          </div>
          <div v-else class="error-list">
            <div v-for="(err, idx) in jobErrors" :key="idx" class="error-item">
              <span class="error-row">第 {{ err.row }} 列</span>
              <span class="error-phone">{{ err.phone || '-' }}</span>
              <span class="error-msg">{{ err.message }}</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="errorModalJob = null">關閉</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { memberService } from '@/api/modules/members.js';
import Swal from 'sweetalert2';

export default {
  name: 'MemberImportHistory',
  setup() {
    const { pageTitle } = usePageTitle('會員匯入 / 匯出紀錄');
    const route = useRoute();
    const router = useRouter();
    const jobs = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const errorModalJob = ref(null);
    const loadingErrors = ref(false);
    const jobErrors = ref([]);
    const revoking = ref(null);
    const revokeModal = ref({ show: false, step: 'loading', jobNumber: null, preview: null });

    const totalPages = computed(() => Math.ceil(total.value / pageSize.value));

    const fetchJobs = async () => {
      loading.value = true;
      error.value = null;
      try {
        const res = await memberService.getImportJobs({ page: currentPage.value, pageSize: pageSize.value });
        if (res?.success && res.data) {
          jobs.value = res.data.items || [];
          total.value = res.data.pagination?.totalItems || 0;
        } else {
          error.value = res?.message || '載入失敗';
        }
      } catch (err) {
        error.value = err?.message || '載入失敗';
      } finally {
        loading.value = false;
      }
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchJobs();
      }
    };

    const startRevoke = async (job) => {
      revokeModal.value = { show: true, step: 'loading', jobNumber: job.jobNumber, preview: null };
      try {
        const res = await memberService.previewRevokeImport(job.jobNumber);
        if (res?.success && res.data) {
          revokeModal.value = { show: true, step: 'preview', jobNumber: job.jobNumber, preview: res.data };
        } else {
          cancelRevoke();
          await Swal.fire({ title: '無法預覽', text: res?.message || '發生錯誤', icon: 'error' });
        }
      } catch (err) {
        cancelRevoke();
        await Swal.fire({ title: '無法預覽', text: err?.response?.data?.message || err?.message || '發生錯誤', icon: 'error' });
      }
    };

    const cancelRevoke = () => {
      revokeModal.value = { show: false, step: 'loading', jobNumber: null, preview: null };
    };

    const confirmRevoke = async () => {
      const jobNumber = revokeModal.value.jobNumber;
      revokeModal.value.step = 'executing';
      revoking.value = jobNumber;
      try {
        const res = await memberService.executeRevokeImport(jobNumber);
        cancelRevoke();
        revoking.value = null;
        if (res?.success && res.data) {
          const d = res.data;
          await Swal.fire({
            title: '撤銷完成',
            html: `<p>已刪除 <strong>${d.deleted}</strong> 位、已停用 <strong>${d.deactivated}</strong> 位、略過 <strong>${d.skipped}</strong> 位（已合併）</p>`,
            icon: 'success'
          });
          await fetchJobs();
        } else {
          await Swal.fire({ title: '撤銷失敗', text: res?.message || '發生錯誤', icon: 'error' });
        }
      } catch (err) {
        cancelRevoke();
        revoking.value = null;
        await Swal.fire({ title: '撤銷失敗', text: err?.response?.data?.message || err?.message || '發生錯誤', icon: 'error' });
      }
    };

    const showErrors = async (job) => {
      errorModalJob.value = job;
      jobErrors.value = [];
      loadingErrors.value = true;
      try {
        const res = await memberService.getImportJob(job.jobNumber);
        if (res?.success && res.data?.errors) {
          jobErrors.value = Array.isArray(res.data.errors) ? res.data.errors : [];
        }
      } catch (err) {
        jobErrors.value = [];
      } finally {
        loadingErrors.value = false;
      }
    };

    const getStatusClass = (status) => {
      const map = {
        processing: 'badge badge-warning',
        completed: 'badge badge-success',
        partial: 'badge badge-warning',
        failed: 'badge badge-danger'
      };
      return map[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const map = {
        processing: '處理中',
        completed: '完成',
        partial: '部分成功',
        failed: '失敗'
      };
      return map[status] || status;
    };

    const getDuplicateStrategyText = (strategy) => {
      return strategy === 'update' ? '更新既有' : '略過';
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-TW', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    };

    // ===================== 匯出紀錄 Tab =====================
    const activeTab = ref(route.query.tab === 'export' ? 'export' : 'import');
    const exportJobs = ref([]);
    const exportLoading = ref(false);
    const exportError = ref(null);
    const exportCurrentPage = ref(1);
    const exportPageSize = ref(20);
    const exportTotal = ref(0);
    const downloadingExport = ref(null);

    const exportTotalPages = computed(() => Math.ceil(exportTotal.value / exportPageSize.value));

    const fetchExportJobs = async () => {
      exportLoading.value = true;
      exportError.value = null;
      try {
        const res = await memberService.getExportJobs({ page: exportCurrentPage.value, pageSize: exportPageSize.value });
        if (res?.success && res.data) {
          exportJobs.value = res.data.items || [];
          exportTotal.value = res.data.pagination?.totalItems || 0;
        } else {
          exportError.value = res?.message || '載入失敗';
        }
      } catch (err) {
        exportError.value = err?.message || '載入失敗';
      } finally {
        exportLoading.value = false;
      }
    };

    const goToExportPage = (page) => {
      if (page >= 1 && page <= exportTotalPages.value) {
        exportCurrentPage.value = page;
        fetchExportJobs();
      }
    };

    const switchTab = (tab) => {
      if (activeTab.value === tab) return;
      activeTab.value = tab;
      router.replace({ query: tab === 'export' ? { tab: 'export' } : {} });
      if (tab === 'export' && exportJobs.value.length === 0) {
        fetchExportJobs();
      }
    };

    const getExportStatusClass = (status) => {
      const map = {
        pending: 'badge badge-secondary',
        processing: 'badge badge-warning',
        completed: 'badge badge-success',
        failed: 'badge badge-danger'
      };
      return map[status] || 'badge badge-secondary';
    };

    const getExportStatusText = (status) => {
      const map = {
        pending: '排隊中',
        processing: '處理中',
        completed: '完成',
        failed: '失敗'
      };
      return map[status] || status;
    };

    const FILTER_STATUS_LABELS = {
      active: '啟用', inactive: '停用', pending: '待審核', blocked: '已封鎖'
    };

    const getFilterSummary = (filters) => {
      if (!filters || Object.keys(filters).length === 0) return '全量匯出';
      const parts = [];
      if (filters.status) parts.push(`狀態=${FILTER_STATUS_LABELS[filters.status] || filters.status}`);
      if (filters.search) parts.push(`關鍵字=${filters.search}`);
      if (filters.tagIds) {
        const count = String(filters.tagIds).split(',').filter(Boolean).length;
        parts.push(`標籤 ${count} 個（${filters.tagMatchType === 'AND' ? '全符合' : '任一'}）`);
      }
      if (filters.registeredStart || filters.registeredEnd) {
        parts.push(`註冊日 ${filters.registeredStart || ''}~${filters.registeredEnd || ''}`);
      }
      if (filters.birthdayStart || filters.birthdayEnd) {
        parts.push(`生日 ${filters.birthdayStart || ''}~${filters.birthdayEnd || ''}`);
      }
      if (filters.birthdayMonth) parts.push(`生日月份=${filters.birthdayMonth} 月`);
      if (filters.birthdayDay) parts.push(`生日日期=${filters.birthdayDay} 日`);
      const address = [filters.addressCity, filters.addressDistrict, filters.addressStreet].filter(Boolean).join('');
      if (address) parts.push(`地址=${address}`);
      return parts.length > 0 ? parts.join('、') : '套用篩選';
    };

    const isExportFileExpired = (job) => {
      if (!job.fileExpiresAt) return false;
      return new Date(job.fileExpiresAt).getTime() < Date.now();
    };

    const downloadExport = async (job) => {
      if (downloadingExport.value) return;
      downloadingExport.value = job.jobNumber;
      try {
        // presigned URL 帶 Content-Disposition: attachment，導航即觸發下載（不受 CORS 限制）
        const response = await memberService.getExportDownloadUrl(job.jobNumber);
        const url = response?.data?.url;
        if (!url) throw new Error('下載連結產生失敗');
        const a = document.createElement('a');
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (err) {
        await Swal.fire({ title: '下載失敗', text: err?.message || '請稍後再試', icon: 'error' });
      } finally {
        downloadingExport.value = null;
      }
    };
    // ===========================================================

    onMounted(() => {
      fetchJobs();
      if (activeTab.value === 'export') {
        fetchExportJobs();
      }
    });

    return {
      pageTitle, jobs, loading, error, currentPage, totalPages, total,
      errorModalJob, loadingErrors, jobErrors,
      revoking, revokeModal,
      fetchJobs, goToPage, showErrors, startRevoke, cancelRevoke, confirmRevoke,
      getStatusClass, getStatusText, getDuplicateStrategyText, formatDate,
      activeTab, switchTab,
      exportJobs, exportLoading, exportError, exportCurrentPage, exportTotalPages, exportTotal,
      downloadingExport,
      fetchExportJobs, goToExportPage, downloadExport,
      getExportStatusClass, getExportStatusText, getFilterSummary, isExportFileExpired
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 900px;
}

.tab-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e5e7eb;
}

.tab-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
}
.tab-item:hover { color: #374151; }
.tab-item.active {
  color: #4f46e5;
  border-bottom-color: #4f46e5;
}

.filters-cell {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #4b5563;
}

.expired-text {
  color: #9ca3af;
  font-size: 13px;
}

.error-summary-text {
  color: #dc2626;
  font-size: 13px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-back {
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  border-radius: 8px;
}

.job-number {
  font-family: monospace;
  font-size: 13px;
  color: #6b7280;
}

.count-cell {
  text-align: center;
  font-weight: 600;
}
.count-cell.success { color: #16a34a; }
.count-cell.info    { color: #2563eb; }
.count-cell.muted   { color: #9ca3af; }
.count-cell.danger  { color: #dc2626; }

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 0;
  color: #9ca3af;
  font-size: 15px;
}
.loading-state i,
.empty-state i {
  font-size: 40px;
}

.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
}
.page-info {
  font-size: 14px;
  color: #6b7280;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 16px;
}
.modal-container {
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow: auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}
.modal-header h3 { margin: 0; font-size: 17px; font-weight: 600; }
.modal-close {
  border: none;
  background: transparent;
  font-size: 22px;
  color: #6b7280;
  cursor: pointer;
}
.modal-body { padding: 16px 24px; }
.modal-footer {
  padding: 12px 24px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
}

.empty-state-small {
  text-align: center;
  padding: 24px;
  color: #9ca3af;
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.error-item {
  display: grid;
  grid-template-columns: 80px 120px 1fr;
  gap: 8px;
  align-items: start;
  padding: 8px 10px;
  background: #fef2f2;
  border-radius: 6px;
  font-size: 13px;
}
.error-row { font-weight: 600; color: #b91c1c; white-space: nowrap; }
.error-phone { color: #6b7280; font-family: monospace; }
.error-msg { color: #374151; }

.action-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.revoke-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.revoke-desc {
  margin: 0 0 20px;
  font-size: 15px;
  color: #374151;
}

.revoke-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.revoke-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 8px;
  border-radius: 10px;
  text-align: center;
  gap: 4px;
}
.revoke-stat.danger  { background: #fef2f2; }
.revoke-stat.warning { background: #fffbeb; }
.revoke-stat.muted   { background: #f9fafb; }

.stat-num {
  font-size: 28px;
  font-weight: 700;
}
.revoke-stat.danger  .stat-num { color: #dc2626; }
.revoke-stat.warning .stat-num { color: #d97706; }
.revoke-stat.muted   .stat-num { color: #9ca3af; }

.stat-label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}
.stat-label small {
  display: block;
  font-size: 11px;
  color: #9ca3af;
  font-weight: 400;
}

.skipped-list {
  margin-bottom: 16px;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
}
.skipped-title {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 8px;
}
.skipped-item {
  font-size: 13px;
  color: #374151;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
}

.revoke-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #fff7ed;
  border-radius: 8px;
  font-size: 14px;
  color: #92400e;
  margin: 0;
}
.revoke-warning i { font-size: 18px; color: #d97706; }

.btn-danger {
  background: #dc2626;
  color: #fff;
  border: 1px solid #dc2626;
}
.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
  border-color: #b91c1c;
}
.btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-outline-danger {
  background: transparent;
  color: #dc2626;
  border: 1px solid #dc2626;
}
.btn-outline-danger:hover:not(:disabled) {
  background: #fef2f2;
}
</style>
